import {
  Typography,
  Grid,
  Box,
  Chip,
  Stack,
  Button,
  Backdrop,
  CircularProgress,
  Modal,
  Paper,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import MenuView from "../../components/MenuView";
import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import useSnack from "../../hooks/useSnack";
import SnackAlert from "../../components/SnackAlert";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import { G, pdf } from "@react-pdf/renderer";
import ProductosPDF from "../../components/ProductosPDF";
import CloseIcon from "@mui/icons-material/Close";
import ProductsCards from "../productos/productsCards";

export default function Productos() {
  const { loadApi } = useApi();
  const [data, setData] = useState({
    hamburguesas: [],
    products: [],
    count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { showSnack, msgSB, openSB, typeSB } = useSnack();
  const [openModal, setOpenModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  function getProducts() {
    setLoading(true);
    loadApi("admin/items", true, "get")
      .then((response) => {
        const productos = {
          hamburguesas: response.data.hamburguesas || [],
          products: response.data.items || [],
          count: response.data.count || 0,
        };
        setData(productos);
      })
      .catch((error) => {
        showSnack(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    getProducts();
  }, []);

  const handleExportPDF = async () => {
    try {
      setPdfLoading(true);
      const blob = await pdf(<ProductosPDF data={data} />).toBlob();

      // Crear un enlace para descargar el archivo
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `productos_pollosteo_${new Date()
        .toLocaleDateString()
        .replace(/\//g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Limpiar después de la descarga
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSnack("PDF generado correctamente", "success");
    } catch (error) {
      showSnack("Error al generar el PDF", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleOpenDetails = (producto) => {
    setSelectedProduct(producto);
    setOpenModal(true);
  };

  const handleCloseDetails = () => {
    setSelectedProduct(null);
    setOpenModal(false);
  };

  return (
    <MenuView>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "100vh", md: "80vh" },
          padding: 2,
        }}
      >
        <Stack
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent={{ xs: "center", sm: "space-between" }}
          sx={{ mb: 2 }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mb: 3,
              textAlign: "center",
              fontSize: { xs: 20, md: 30 },
            }}
          >
            Total de Productos: {data.count}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={handleExportPDF}
            disabled={pdfLoading || data.length === 0}
            sx={{
              backgroundColor: "#f9de4b",
              color: "black",
              textDecoration: "none",
              padding: "5px 10px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              border: "1px solid #f9de4b",
            }}
          >
            {pdfLoading ? "Generando..." : "Exportar a PDF"}
            <PdfIcon sx={{ ml: 1 }} />
          </Button>
        </Stack>

        {data.count === 0 ? (
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              fontSize: { xs: "1rem", sm: "1.5rem" },
              color: "#555",
              fontWeight: "bold",
              mt: 2,
            }}
          >
            No hay productos disponibles
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={5.5}>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                Hamburguesas
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
                {data.hamburguesas.map((producto, key) => (
                  <Grid item xs={12} sm={6} lg={4} key={key}>
                    <ProductsCards
                      data={[producto]}
                      handleOpenDetails={handleOpenDetails}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              md={0.5}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: { xs: "block", md: "none" },
                my: 2,
              }}
            >
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                Otros Productos
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
                {data.products.map((producto, key) => (
                  <Grid item xs={12} sm={6} lg={4} key={key}>
                    <ProductsCards
                      data={[producto]}
                      handleOpenDetails={handleOpenDetails}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        )}

        <Modal
          open={openModal}
          onClose={handleCloseDetails}
          aria-labelledby="modal-detalles"
        >
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 400 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" component="h2">
                {selectedProduct?.name}
              </Typography>
              <IconButton onClick={handleCloseDetails} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedProduct?.description?.split(",").map((sabor, index) => {
                const [numeroSabor, tipoSabor] = sabor
                  .split("::")
                  .map((s) => s.trim());
                return (
                  <Chip
                    key={index}
                    label={`${numeroSabor}: ${tipoSabor}`}
                    size="small"
                    sx={{
                      fontSize: 12,
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                );
              })}
            </Box>
          </Paper>
        </Modal>

        <Backdrop
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
      </Box>
    </MenuView>
  );
}
